/**
 * ProofHistoryPanel — Passport-resident proof upload history.
 *
 * Operator brief (Feb 13, 2026): "Show recent proof history inside
 * Passport / Proof History, not constantly on the main screen.
 * This supports the Passport evidence record."
 *
 * Renders a calm list of the learner's recent proof analyses:
 *   <competency> — <outcome> · <when>
 * Outcomes mapped to operator-locked vocabulary:
 *   awaiting_analysis    → "Awaiting analysis"
 *   awaiting_credits     → "Awaiting credits — your upload is saved"
 *   analysis_complete    → derived from the proof_evidence verdict
 *   denied (ledger row)  → "Paused — out of proof credits"
 *
 * Renders nothing when there's no history yet (clean Passport).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Coins, ShieldCheck, Circle, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;


const OUTCOME_LABEL = {
  consumed:  { label: "Proof analysis charged", Icon: ShieldCheck, tone: "text-emerald-700" },
  tracked:   { label: "Proof analysis logged", Icon: ShieldCheck, tone: "text-slate-600" },
  denied:    { label: "Paused — out of proof credits", Icon: Coins, tone: "text-amber-700" },
};

const COMPETENCY_LABEL = {
  roundabouts: "Roundabouts",
  junctions: "Junctions",
  road_positioning: "Road Positioning",
};


export default function ProofHistoryPanel() {
  const [items, setItems] = useState(null);
  const [showInvisible, setShowInvisible] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/proof-entitlements/me`, {
          withCredentials: true,
        });
        if (!alive) return;
        const ledger = r.data?.recent_ledger || [];
        const enforced = !!r.data?.balance?.show_balance_to_user;
        setShowInvisible(!enforced);
        setItems(ledger);
      } catch (_e) {
        if (alive) setItems([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (items === null) {
    return (
      <section className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5"
               data-testid="proof-history-panel-loading">
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 size={14} className="animate-spin" />
          Loading proof history…
        </div>
      </section>
    );
  }
  if (items.length === 0) return null;

  return (
    <section
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5"
      data-testid="proof-history-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-extrabold text-slate-900 dark:text-white">
            Proof history
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            Your AI proof analyses — recent first.
          </p>
        </div>
        <ShieldCheck className="w-4 h-4 text-slate-400" />
      </div>

      <ul className="space-y-2" data-testid="proof-history-list">
        {items.slice(0, 10).map((row, i) => {
          const o = OUTCOME_LABEL[row.outcome] || {
            label: "Recorded", Icon: Circle, tone: "text-slate-500",
          };
          const Icon = o.Icon;
          const compLabel = COMPETENCY_LABEL[row.competency_id] || row.competency_id || "—";
          // Hide "tracked" rows from fast_track learners by default
          // (operator: invisible tracking). Admin / debug surfaces
          // can still pull from /admin/user/{id}.
          if (row.outcome === "tracked" && !showInvisible) return null;
          return (
            <li
              key={row.id || i}
              className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-100 bg-slate-50/60"
              data-testid={`proof-history-row-${i}`}
              data-outcome={row.outcome}
            >
              <div className="flex items-start gap-2 min-w-0">
                <Icon size={16} className={`${o.tone} flex-shrink-0 mt-0.5`} />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">
                    {compLabel}
                  </div>
                  <div className={`text-[11px] ${o.tone} truncate`}>
                    {o.label}
                  </div>
                </div>
              </div>
              <div className="text-[11px] text-slate-400 flex-shrink-0">
                {formatRelative(row.at)}
              </div>
            </li>
          );
        })}
      </ul>

      <p className="text-[11px] text-slate-400 italic mt-3">
        Each proof analysis uses 1 proof credit. Your uploads are
        always saved — if you run out of credits, analysis pauses
        until credits are added.
      </p>
    </section>
  );
}

function formatRelative(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
    return `${Math.round(diff / 86_400_000)}d ago`;
  } catch {
    return "";
  }
}
